import { useRef, type ReactNode } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const MEDIA = {
  allowMotion: "(prefers-reduced-motion: no-preference)",
  landscape: "(min-width: 768px) and (min-height: 561px) and (min-aspect-ratio: 1/1)",
};

const MOTION = {
  normal: 0.64,
  macro: 1,
  ease: "power3.out",
  /* pousar: overshoot curto, só pro que é objeto na composição (carimbo, selo,
     polaroid, etiqueta). Espelha o --ease-pop do CSS. */
  pop: "back.out(1.5)",
  /* traço: sem overshoot, senão a linha passa do fim e volta */
  draw: "power2.out",
} as const;

/* Magnetismo bem fraco: só alinha quando o scroll já está a poucos px do topo
   de uma seção. Nunca puxa o usuário de um meio de rolagem pra outra seção. */
const SECTION_MAGNETISM = {
  thresholdPx: 56,
  delay: 0.24,
  duration: { min: 0.1, max: 0.22 },
  ease: "power1.out",
} as const;

/* Gestos manuscritos (asterisco + linha, réguas, rabiscos). O tween genérico de
   `gesture` é fade + escala, que numa linha de 2px não se vê; estes desenham. */
const DRAWABLE = ".hero-connector, .clinic-connector, .conductor-rule, .amulet-rule";

type Q = (selector: string) => HTMLElement[];

/** Traço feito à mão: quando o asterisco abre o gesto ele pousa primeiro e a
 *  linha corre a partir dele; quando ele fecha, a linha corre até o asterisco e
 *  ele pousa na chegada. A ordem no DOM diz qual dos dois casos é. Elemento sem
 *  filhos (régua, rabisco) é ele mesmo o traço. */
function drawGesture(tl: gsap.core.Timeline, el: HTMLElement, at: number) {
  const line = el.querySelector<HTMLElement>("i");
  const star = el.querySelector<HTMLElement>("b");

  if (!line && !star) {
    tl.from(el, {
      scaleX: 0,
      transformOrigin: "left center",
      duration: 0.6,
      ease: MOTION.draw,
    }, at);
    return;
  }

  const starFirst = el.firstElementChild?.tagName === "B";

  if (star) {
    tl.from(star, {
      opacity: 0,
      scale: 0.4,
      rotation: -60,
      transformOrigin: "50% 50%",
      duration: 0.42,
      ease: MOTION.pop,
    }, starFirst ? at : at + 0.38);
  }
  if (line) {
    tl.from(line, {
      scaleX: 0,
      transformOrigin: "left center",
      duration: 0.52,
      ease: MOTION.draw,
    }, starFirst ? at + 0.16 : at);
  }
}

type PageAnimationsProps = {
  children: ReactNode;
};

export function PageAnimations({ children }: PageAnimationsProps) {
  const scope = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    const root = scope.current;
    if (!root) return;

    ScrollTrigger.config({ ignoreMobileResize: true });

    const ctaMobile = document.querySelector<HTMLElement>("#cta-mobile");
    if (ctaMobile) {
      let depoisDoHero = false;
      let investimentoNaTela = false;
      let depoimentosNaTela = false;
      let fechamentoNaTela = false;
      const sync = () => ctaMobile.toggleAttribute(
        "data-visible",
        depoisDoHero && !investimentoNaTela && !depoimentosNaTela && !fechamentoNaTela,
      );

      ScrollTrigger.create({
        trigger: "#o-que-e",
        start: "top top",
        end: "max",
        onToggle: (self) => {
          depoisDoHero = self.isActive;
          sync();
        },
      });
      ScrollTrigger.create({
        trigger: "#investimento",
        start: "top 80%",
        end: "bottom 20%",
        onToggle: (self) => {
          investimentoNaTela = self.isActive;
          sync();
        },
      });
      ScrollTrigger.create({
        trigger: "#depoimentos",
        start: "top 80%",
        end: "bottom 20%",
        onToggle: (self) => {
          depoimentosNaTela = self.isActive;
          sync();
        },
      });
      ScrollTrigger.create({
        trigger: "#fechamento",
        start: "top 80%",
        end: "bottom 20%",
        onToggle: (self) => {
          fechamentoNaTela = self.isActive;
          sync();
        },
      });
    }

    const select = gsap.utils.selector(root);
    const media = gsap.matchMedia();

    media.add(MEDIA, (mediaContext) => {
      const { allowMotion, landscape } = mediaContext.conditions as {
        allowMotion: boolean;
        landscape: boolean;
      };
      if (!allowMotion) return;

      const sections = select<HTMLElement>(".poster-section");

      ScrollTrigger.create({
        id: "section-magnetism",
        start: 0,
        end: "max",
        snap: {
          snapTo: (progress) => {
            const maxScroll = ScrollTrigger.maxScroll(window);
            if (maxScroll <= 0) return progress;

            const scrollTop = progress * maxScroll;
            const { thresholdPx } = SECTION_MAGNETISM;
            let alignedProgress = progress;
            let nearestGap = thresholdPx + 1;

            for (const section of sections) {
              const sectionTop = section.getBoundingClientRect().top + window.scrollY;
              const gap = Math.abs(scrollTop - sectionTop);
              if (gap <= thresholdPx && gap < nearestGap) {
                nearestGap = gap;
                alignedProgress = gsap.utils.clamp(0, 1, sectionTop / maxScroll);
              }
            }

            return alignedProgress;
          },
          delay: SECTION_MAGNETISM.delay,
          duration: SECTION_MAGNETISM.duration,
          ease: SECTION_MAGNETISM.ease,
          directional: false,
          inertia: false,
        },
      });
      root.dataset.sectionMagnetism = "active";

      /* Abre um timeline preso à entrada da seção. `once` porque a página é de
         venda: o movimento apresenta o pôster uma vez e sai do caminho. */
      const onEnter = (
        selector: string,
        build: (tl: gsap.core.Timeline, q: Q) => void,
        start = "clamp(top 84%)",
      ) => {
        const section = root.querySelector<HTMLElement>(selector);
        if (!section) return;
        const tl = gsap.timeline({
          defaults: { duration: MOTION.normal, ease: MOTION.ease },
          scrollTrigger: { trigger: section, start },
        });
        build(tl, gsap.utils.selector(section) as Q);
      };

      /* ── Hero ─────────────────────────────────────────────────────────────
         O header vive fora de #page-clip (overflow no root quebra position
         fixed no Safari iOS), então é o único alvo buscado no document. */
      const hero = gsap.timeline({ defaults: { ease: MOTION.ease } });
      hero
        .addLabel("portrait")
        .from(".site-header-inner > *", {
          autoAlpha: 0,
          y: -10,
          duration: 0.52,
          stagger: 0.08,
        }, "portrait")
        .from('[data-anim="hero-foto"]', {
          autoAlpha: 0,
          clipPath: "inset(0 0 8% 0)",
          scale: 1.025,
          duration: MOTION.macro,
        }, "portrait")
        .from('[data-anim="l1"], [data-anim="l3"]', {
          yPercent: 40,
          autoAlpha: 0,
          duration: 0.74,
          stagger: 0.08,
        }, "portrait+=0.18")
        .from('[data-anim="hero-label"]', {
          autoAlpha: 0,
          y: 14,
          duration: 0.52,
          stagger: 0.07,
        }, "portrait+=0.38")
        .fromTo(".hero-circled ellipse", {
          strokeDasharray: 1,
          strokeDashoffset: 1,
        }, {
          strokeDashoffset: 0,
          duration: 0.72,
          ease: "power2.inOut",
        }, "portrait+=0.56");

      const heroConnector = root.querySelector<HTMLElement>(".hero-connector");
      if (heroConnector) drawGesture(hero, heroConnector, 0.62);

      /* carimbo do canto: entra por último, é assinatura e não protagonista */
      const monogram = root.querySelector<HTMLElement>(".poster-monogram");
      if (monogram) {
        hero.from(monogram, {
          autoAlpha: 0,
          scale: 1.3,
          rotation: -10,
          transformOrigin: "50% 50%",
          duration: 0.5,
          ease: MOTION.pop,
        }, 0.86);
      }

      /* crédito manuscrito do retrato: só existe no mobile, escrito depois que
         a foto já está no lugar */
      const heroCredit = root.querySelector<HTMLElement>(".hero-photo-credit");
      if (heroCredit && !landscape) {
        hero.from(heroCredit, { autoAlpha: 0, y: 8, duration: 0.5 }, 0.78);
      }

      if (landscape) {
        gsap.to('[data-anim="hero-foto"] img', {
          yPercent: 4,
          ease: "none",
          scrollTrigger: {
            trigger: "#topo",
            start: "top top",
            end: "bottom top",
            scrub: 0.6,
          },
        });
      } else {
        /* No retrato a foto não tem coluna pra deslizar, então quem dá
           profundidade é o enquadramento abrindo devagar enquanto o hero sai. */
        gsap.to(".hero-photo-zoom", {
          scale: "+=0.05",
          ease: "none",
          scrollTrigger: {
            trigger: "#topo",
            start: "top top",
            end: "bottom top",
            scrub: 0.6,
          },
        });
      }

      /* ── Entrada genérica de seção ───────────────────────────────────────
         media, lista e gesto passaram a valer também no retrato: o celular é
         onde a página mais é lida e era onde quase nada se movia. */
      select<HTMLElement>(".poster-section:not(#topo)").forEach((section) => {
        const within = gsap.utils.selector(section);
        const brand = within<HTMLElement>('[data-motion="brand"]');
        const title = within<HTMLElement>('[data-motion="title"]');
        const titleLines = within<HTMLElement>('[data-motion="title"] > span');
        const lead = within<HTMLElement>('[data-motion="lead"]');
        const gestures = within<HTMLElement>('[data-motion="gesture"]');
        const mediaItems = within<HTMLElement>('[data-motion="media"]');
        const listItems = within<HTMLElement>("[data-motion-list] > *");

        const drawn = gestures.filter((el) => el.matches(DRAWABLE));
        const gesture = gestures.filter((el) => !el.matches(DRAWABLE));

        if (![brand, title, lead, gestures, mediaItems, listItems].some((items) => items.length)) return;

        const entrance = gsap.timeline({
          /* clearProps no default: terminada a revelação, o elemento devolve o
             estilo ao CSS em vez de carregar `opacity:1; transform:none` inline
             pra sempre. Não é só higiene — estilo inline vence regra de :hover e
             de media query, então sem isso o hover dos cards e da polaroid
             morreria justamente depois da seção entrar. */
          defaults: { duration: MOTION.normal, ease: MOTION.ease, clearProps: "opacity,transform,clipPath" },
          scrollTrigger: {
            trigger: section,
            start: "clamp(top 86%)",
          },
        });
        entrance.addLabel("enter");

        if (brand.length) entrance.from(brand, { opacity: 0, y: 8, duration: 0.42 }, "enter");

        /* Título quebrado em três linhas ou mais (o fecho da página) entra linha
           a linha, fechando com o mesmo gesto que abriu o hero. Título de uma ou
           duas partes continua entrando como bloco: escalonar "A TURMA" e
           "de 2026", que dividem a mesma linha, só desmontaria a palavra. */
        if (titleLines.length >= 3) {
          entrance.from(titleLines, { opacity: 0, yPercent: 30, duration: 0.6, stagger: 0.07 }, "enter+=0.06");
        } else if (title.length) {
          entrance.from(title, { opacity: 0, yPercent: 14 }, "enter+=0.06");
        }

        if (lead.length) entrance.from(lead, { opacity: 0, y: 18 }, "enter+=0.16");

        if (gesture.length) {
          entrance.from(gesture, {
            opacity: 0,
            scale: 0.94,
            rotation: -1.5,
            transformOrigin: "50% 50%",
          }, "enter+=0.12");
        }
        drawn.forEach((el) => drawGesture(entrance, el, 0.18));

        /* A polaroid é o caso caro: a foto mora dentro de um papel com
           mask-image e a figure carrega drop-shadow. Animar clip-path ali
           obriga o navegador a rasterizar a máscara e a sombra a cada quadro,
           e no desktop isso aparece como travada na entrada das convidadas.
           Ela sai do lote de clip-path e revela só por opacidade e escala,
           que o compositor resolve sozinho. */
        const mediaPolaroid = mediaItems.filter((el) => el.closest(".polaroid"));
        const mediaComuns = mediaItems.filter((el) => !el.closest(".polaroid"));

        if (mediaComuns.length) {
          entrance.from(mediaComuns, {
            opacity: 0,
            /* clip-path numa foto de tela cheia é caro no celular; lá o reveal
               é só opacidade e um respiro de escala */
            ...(landscape
              ? { clipPath: "inset(0 0 8% 0)", scale: 1.018 }
              : { scale: 1.012 }),
            duration: landscape ? 0.82 : 0.6,
          }, "enter+=0.1");
        }

        if (mediaPolaroid.length) {
          entrance.from(mediaPolaroid, {
            opacity: 0,
            scale: 1.014,
            duration: landscape ? 0.7 : 0.6,
          }, "enter+=0.1");
        }

        if (listItems.length) {
          entrance.from(listItems, {
            opacity: 0,
            y: 14,
            duration: 0.5,
            stagger: 0.055,
          }, "enter+=0.22");
        }
      });

      /* Selo terapêutico: o corpo já entra pelo [data-motion="lead"] genérico.
         Um segundo from() aqui capturava opacity 0 (já aplicada pelo primeiro)
         como destino e o texto ficava invisível. */

      /* ── Quem conduz ────────────────────────────────────────────────────── */
      onEnter("#quem-conduz", (tl, q) => {
        const paras = q(".conductor-bio p");
        if (paras.length) {
          tl.from(paras, { opacity: 0, y: 12, duration: 0.5, stagger: 0.08 }, 0.22);
        }
      });

      /* ── Ciclos ─────────────────────────────────────────────────────────── */
      select<HTMLElement>(".cycle").forEach((cycle) => {
        const q = gsap.utils.selector(cycle) as Q;

        /* o numeral vazado corre mais devagar que o texto: é o que dá ao
           percurso a sensação de camada funda atravessando os quatro pôsteres */
        const ghost = q(".cycle-ghost-number");
        if (ghost.length) {
          gsap.to(ghost, {
            yPercent: landscape ? -7 : -4,
            ease: "none",
            scrollTrigger: { trigger: cycle, start: "top bottom", end: "bottom top", scrub: 0.6 },
          });
        }

        const tl = gsap.timeline({
          defaults: { duration: MOTION.normal, ease: MOTION.ease },
          scrollTrigger: { trigger: cycle, start: "clamp(top 84%)" },
        });

        /* o trilho se desenha e os meses são marcados um a um em cima dele */
        const rail = q(".cycle-timeline");
        if (rail.length) tl.from(rail, { "--rail": 0, duration: 0.85, ease: MOTION.draw }, 0.24);

        const nodes = q(".cycle-node");
        if (nodes.length) {
          tl.from(nodes, {
            scale: 0.5,
            opacity: 0,
            transformOrigin: "50% 50%",
            duration: 0.42,
            ease: MOTION.pop,
            stagger: 0.08,
          }, 0.32);
        }

        /* a polaroid é colada no papel agora, não impressa junto com ele.
           clearProps devolve o transform ao CSS pro hover do desktop assumir. */
        const polaroid = q(".polaroid");
        if (polaroid.length) {
          tl.from(polaroid, {
            opacity: 0,
            y: 16,
            rotation: "+=3.5",
            duration: 0.72,
            ease: MOTION.pop,
            clearProps: "transform",
          }, 0.18);
        }
      });

      /* ── Como funciona ──────────────────────────────────────────────────── */
      onEnter("#como-funciona", (tl, q) => {
        /* tipos móveis caindo na composição */
        const numbers = q(".how-number");
        if (numbers.length) {
          tl.from(numbers, {
            opacity: 0,
            y: 12,
            rotation: -5,
            transformOrigin: "50% 100%",
            duration: 0.52,
            ease: MOTION.pop,
            stagger: 0.06,
          }, 0.28);
        }

        /* o amarelo do item em destaque é varrido como marca-texto: leva o olho
           ao que a lista está de fato vendendo */
        const featured = q(".how-list li.is-featured");
        if (featured.length) tl.from(featured, { backgroundSize: "0% 100%", duration: 0.8, ease: MOTION.draw }, 0.5);
      });

      /* ── Investimento ───────────────────────────────────────────────────── */
      onEnter("#investimento", (tl, q) => {
        /* o card do anual é sticker colado por cima da página: pousa pesado */
        const annual = q(".annual-card");
        if (annual.length) {
          tl.from(annual, {
            opacity: 0,
            y: 14,
            scale: 1.035,
            rotation: "+=1.4",
            transformOrigin: "50% 30%",
            duration: 0.75,
            ease: MOTION.pop,
            clearProps: "transform",
          }, 0.08);
        }

        /* o recibo entra com a sombra dura nascendo do zero. clearProps na
           sombra é obrigatório: box-shadow inline venceria o :hover do CSS. */
        const quarter = q(".quarter-card");
        if (quarter.length) {
          tl.from(quarter, {
            opacity: 0,
            y: 16,
            rotation: "+=1.6",
            boxShadow: "0px 0px 0 rgba(20, 20, 20, 0)",
            duration: 0.66,
            ease: MOTION.pop,
            clearProps: "transform,boxShadow",
          }, 0.2);
        }

        const star = q(".investment-star");
        if (star.length) {
          tl.from(star, {
            opacity: 0,
            rotation: -90,
            scale: 0.5,
            transformOrigin: "50% 50%",
            duration: 0.5,
            ease: MOTION.pop,
          }, 0.3);
        }

        /* selo manuscrito acrescentado depois que o card assentou */
        const seal = q(".annual-seal");
        if (seal.length) {
          tl.from(seal, {
            opacity: 0,
            y: 6,
            rotation: -6,
            transformOrigin: "0% 50%",
            duration: 0.45,
            ease: MOTION.pop,
          }, 0.6);
        }

        /* o círculo do colar se desenha: é o único objeto físico da oferta */
        const necklace = q(".necklace-reference");
        if (necklace.length) tl.from(necklace, { "--draw": 0, duration: 0.8, ease: "power2.inOut" }, 0.72);

        /* dois piscos no marcador de nota, guiando o olho até o texto legal.
           Nunca em loop: aviso que pisca sem parar vira ruído, não aviso. */
        const sups = q(".annual-price sup, .quarter-price sup");
        if (sups.length) {
          tl.to(sups, {
            opacity: 0.3,
            duration: 0.34,
            repeat: 3,
            yoyo: true,
            ease: "power1.inOut",
          }, 1.5);
        }
      });

      /* ── Amuleto ────────────────────────────────────────────────────────── */
      onEnter("#amuleto", (tl, q) => {
        const paper = q(".amulet-paper");
        if (paper.length) {
          tl.from(paper, {
            opacity: 0,
            y: 18,
            rotation: "+=3",
            duration: 0.8,
            ease: MOTION.pop,
            clearProps: "transform",
          }, 0);
        }

        const tag = q(".amulet-tag");
        if (tag.length) {
          tl.from(tag, {
            opacity: 0,
            scale: 1.2,
            rotation: "+=5",
            transformOrigin: "0% 50%",
            duration: 0.42,
            ease: MOTION.pop,
          }, 0.58);
        }
      });

      /* ── Depoimentos ────────────────────────────────────────────────────── */
      onEnter("#depoimentos", (tl, q) => {
        /* a seta rabiscada se desenha de cima pra baixo, apontando da nota até
           o primeiro espaço vazio (variável CSS: o alvo é um pseudo-elemento) */
        const note = q(".testimonials-note");
        if (note.length) tl.from(note, { "--arrow-draw": 0, duration: 0.75, ease: MOTION.draw }, 0.5);
      });

      /* ── FAQ ────────────────────────────────────────────────────────────── */
      onEnter("#faq", (tl, q) => {
        /* marca-texto rasgado passado sobre a pergunta principal */
        const hl = q('.faq-featured [data-slot="accordion-trigger"]');
        if (hl.length) tl.from(hl, { backgroundSize: "0% 100%", duration: 0.72, ease: MOTION.draw }, 0.3);
      });

      /* ── Fechamento ─────────────────────────────────────────────────────── */
      onEnter("#fechamento", (tl, q) => {
        /* primeiro a oferta principal, depois a alternativa */
        const actions = q(".closing-actions a");
        if (actions.length) tl.from(actions, { opacity: 0, y: 12, duration: 0.5, stagger: 0.08 }, 0.45);
      });

      /* ── Chegada por âncora ─────────────────────────────────────────────
         Todo CTA da página aponta pra #investimento. Sem isso a página só para
         de rolar e a leitora tem que descobrir sozinha o que o botão prometeu.
         O clique é o gatilho, não o hashchange: clicar no mesmo hash em que já
         se está não dispara hashchange nenhum. */
      const flashInvestimento = () => {
        const card = root.querySelector<HTMLElement>(".annual-card");
        if (!card) return;
        gsap.fromTo(card,
          { boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.05)" },
          {
            boxShadow: "0 0 0 5px rgba(244, 228, 55, 0.95)",
            duration: 0.28,
            delay: 0.5,
            ease: "power2.out",
            yoyo: true,
            repeat: 1,
            clearProps: "boxShadow",
          });
      };
      const ctas = Array.from(document.querySelectorAll<HTMLAnchorElement>('a[href="#investimento"]'));
      ctas.forEach((cta) => cta.addEventListener("click", flashInvestimento));

      return () => {
        ctas.forEach((cta) => cta.removeEventListener("click", flashInvestimento));
        root.removeAttribute("data-section-magnetism");
      };
    });

    let active = true;
    document.fonts?.ready.then(() => {
      if (!active) return;
      ScrollTrigger.refresh();
      const target = window.location.hash && document.querySelector(window.location.hash);
      if (target) window.scrollTo(0, target.getBoundingClientRect().top + window.scrollY);
    });

    return () => {
      active = false;
      ctaMobile?.removeAttribute("data-visible");
      root.removeAttribute("data-section-magnetism");
      media.revert();
    };
  }, { scope });

  return <div ref={scope} id="page-clip" className="overflow-x-clip">{children}</div>;
}
